import React, { type ReactElement } from 'react';
import { monitor } from '@atlassian/jira-portfolio-3-common/src/analytics/performance';
import {
	REPARENT_CMP_TO_TMP_WARNING,
	REPARENT_TMP_TO_EXTERNAL_WARNING,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/flags';
import { TABLE_ISSUE } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/table';
import type { Sorting } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/visualisations/types';
import type { FlagActionPayload } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/flags/actions';
import { getBody } from '@atlassian/jira-portfolio-3-portfolio/src/common/dom';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import { PROFILE_KEYS } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import {
	type OnMove,
	type SortDelta,
	type DropState,
	type Issues,
	DROP_CHILD,
	DROP_INVALID_CHILD,
	DROP_INVALID_SIBLING,
	DROP_NONE,
	DROP_SIBLING,
	REPARENT_TMP_TO_EXTERNAL_ERROR,
	REPARENT_CMP_TO_TMP_ERROR,
} from './types';
import { isSortByRank } from './util';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type { SortEvent, SortEventWithTag } from 'react-sortable-hoc/types';

export const disabledElements = ['input', 'textarea', 'select', 'option', 'button'];

export const shouldCancelStart = (disableDnD?: boolean) => (e: SortEvent | SortEventWithTag) => {
	if (isDefined(disableDnD) && disableDnD) {
		return true;
	}

	if (
		!(e.target instanceof HTMLElement) ||
		disabledElements.indexOf(e.target.tagName.toLowerCase()) !== -1
	) {
		return true;
	}

	let node = e.target;

	while (node) {
		if (node.dataset && node.dataset.role === 'header') {
			return true;
		}
		// @ts-expect-error - TS2322 - Type 'ParentNode | null' is not assignable to type 'HTMLElement'.
		node = node.parentNode;
	}

	return false;
};

export const componentDidUpdateHelper = (
	state: DropState,
	getDraggedNode: () => Element | undefined,
	isLoading: boolean,
) => {
	const { type: dropType } = state;

	const node = getDraggedNode();

	if (!isLoading) {
		monitor.finishProfile(PROFILE_KEYS.RENDER.SCOPE_TABLE);
	}

	if (!node) return;

	switch (dropType) {
		case DROP_NONE:
		case DROP_CHILD:
		case DROP_SIBLING: {
			node.classList.remove(styles.isInvalid);
			return;
		}
		case DROP_INVALID_CHILD:
		case DROP_INVALID_SIBLING: {
			node.classList.add(styles.isInvalid);
			return;
		}
		default: {
			throw new Error(`Unexpected drop type: ${dropType}`);
		}
	}
};

export const disableUserSelection = (disableSelection: boolean) => {
	const body = getBody();
	body.classList.toggle(styles.unselected, disableSelection);
};

const truncateSummary = (originalSummary: string, maxLength = 50): string => {
	if (originalSummary.length > maxLength) {
		return `${originalSummary.slice(0, maxLength - 3)}...`;
	}
	return originalSummary;
};

const getIssueKey = (
	projectKey: string,
	issueNum: number | undefined,
	summary: string,
): ReactElement => {
	const isScenarioIssue = issueNum === undefined;
	return <b>{isScenarioIssue ? truncateSummary(summary) : `${projectKey}-${issueNum}`}</b>;
};

export const onSortEndHelper = (
	state: DropState,
	onMove: OnMove,
	sorting: Sorting,
	onInvalidRanking: (payload: FlagActionPayload) => void,
	issues: Issues,
	delta: SortDelta,
): DropState => {
	disableUserSelection(false);

	const { newIndex, oldIndex } = delta;
	const { group, id, relation, anchor, parentId, type, errorType } = state;
	const dragAndDrop = true;
	if (newIndex !== oldIndex && id && relation && isSortByRank(sorting.field)) {
		onMove({ anchor, dragAndDrop, group, id, parentId, relation });
	}

	if (
		type === DROP_INVALID_CHILD &&
		(errorType === REPARENT_TMP_TO_EXTERNAL_ERROR || errorType === REPARENT_CMP_TO_TMP_ERROR)
	) {
		const indexBefore = newIndex - (newIndex > oldIndex ? 0 : 1);
		const draggedIssue = issues[oldIndex];
		const parentIssue = issues[indexBefore];

		onInvalidRanking({
			key:
				errorType === REPARENT_TMP_TO_EXTERNAL_ERROR
					? REPARENT_TMP_TO_EXTERNAL_WARNING
					: REPARENT_CMP_TO_TMP_WARNING,
			context:
				draggedIssue.tag === TABLE_ISSUE && parentIssue.tag === TABLE_ISSUE
					? {
							issueKey: getIssueKey(
								draggedIssue.value.project.key,
								draggedIssue.value.issueKey,
								draggedIssue.value.summary,
							),
							projectName: draggedIssue.value.project.name,
							destinationProjectName: parentIssue.value.project.name,
						}
					: undefined,
		});
	}

	return {
		anchor: null,
		group: null,
		id: null,
		parentId: null,
		relation: null,
		type: DROP_NONE,
		errorType: null,
	};
};

export const getDropClassName = (issues: Issues, drop: DropState) => {
	const { type, parentId } = drop;
	let dropRootIndex = 0;
	if (isDefined(parentId)) {
		const dropParent = issues.find(
			(issue) => issue.tag === TABLE_ISSUE && issue.value.id === parentId,
		);
		if (dropParent && dropParent.tag === TABLE_ISSUE) {
			dropRootIndex = dropParent.value.rootIndex || 0;
		}
	}

	switch (type) {
		case DROP_NONE: {
			return;
		}
		case DROP_INVALID_SIBLING: {
			return styles['drop-invalid-sibling'];
		}
		case DROP_INVALID_CHILD: {
			return dropRootIndex % 2 === 0
				? styles['drop-invalid-child-odd']
				: styles['drop-invalid-child-even'];
		}
		case DROP_SIBLING: {
			return styles['drop-sibling'];
		}
		case DROP_CHILD: {
			return dropRootIndex % 2 === 0 ? styles['drop-child-odd'] : styles['drop-child-even'];
		}
		default: {
			throw new Error(`Unexpected drop type: ${type}`);
		}
	}
};

// NOTE sortable-hoc doesn't provide an API to control dragged clone
// but we can easily find its DOM node and style it
// this will gone after putting back beautiful-dnd as soon as it gets support for virtualization
export const getDraggedNode = () => {
	const body = getBody();
	// Unable to use @ts-expect-error it conflicts with typecheck with project references according to which ts-expect error is not needed and existing typecheck requires it, hence ignoring it till TS project references are enabled
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore - error TS2488: Type 'HTMLCollection' must have a '[Symbol.iterator]()' method that returns an iterator.
	for (const node of body.children) {
		// Unable to use @ts-expect-error it conflicts with typecheck with project references according to which ts-expect error is not needed and existing typecheck requires it, hence ignoring it till TS project references are enabled
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore - TS2339 - Property 'dataset' does not exist on type 'Element'.
		if (node.dataset.issue) {
			return node;
		}
	}
};
